/**
 * Portal Pipeline view (SPEC E4 R3, AC4 — reader contract: E6 pipeline def + E3
 * summary).
 *
 * Renders the tenant's resolved pipeline (E6 tenant_pipeline_definitions, or the
 * synthesized default) with per-stage counts + value from the E3 pipeline
 * summary. When there are no opportunities to render (E0 backfill not yet run —
 * e.g. dvw4ea's pipeline view stays gated until then), it degrades to a
 * "Pipeline coming soon" placeholder so a mis-toggle fails safe (AC4).
 */

import { useQuery } from '@tanstack/react-query'
import {
  getPortalPipelineDefinition,
  getPortalPipelineSummary,
} from '@/lib/api/portal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

function ComingSoon() {
  return (
    <div
      className="rounded-md border border-dashed p-8 text-center"
      data-testid="pipeline-coming-soon"
    >
      <p className="text-sm font-medium text-foreground">Pipeline coming soon</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Your deal pipeline will appear here once your opportunities are set up.
      </p>
    </div>
  )
}

export function PortalPipeline() {
  const defQuery = useQuery({
    queryKey: ['portal', 'pipeline', 'definition'],
    queryFn: () => getPortalPipelineDefinition(),
  })
  const summaryQuery = useQuery({
    queryKey: ['portal', 'pipeline', 'summary'],
    queryFn: () => getPortalPipelineSummary(),
  })

  if (defQuery.isLoading || summaryQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading pipeline…</p>
  }

  // Hard-degrade to the placeholder on any error OR when the pipeline has no
  // opportunities yet (the AC4 / dvw4ea-gate fail-safe).
  const pipeline = defQuery.data?.pipeline
  const summary = summaryQuery.data
  const stages = pipeline?.stages ?? []

  const countByStage = new Map<string, number>()
  const valueByStage = new Map<string, number>()
  let totalCount = 0
  for (const s of summary?.stages ?? []) {
    const c = s.count ?? 0
    countByStage.set(s.stage_key, c)
    valueByStage.set(s.stage_key, s.total_value ?? 0)
    totalCount += c
  }

  if (defQuery.isError || summaryQuery.isError || stages.length === 0 || totalCount === 0) {
    return <ComingSoon />
  }

  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="portal-pipeline"
    >
      {stages.map((stage) => (
        <Card key={stage.stage_key}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: stage.color || '#94a3b8' }}
                aria-hidden
              />
              {stage.display_label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {countByStage.get(stage.stage_key) ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">
              {valueByStage.get(stage.stage_key)
                ? `$${(valueByStage.get(stage.stage_key) ?? 0).toLocaleString()}`
                : 'No value set'}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
